// Device-held ECDSA signing key ("biometric-protected key").
// SECURITY: the private key is a NON-EXTRACTABLE WebCrypto CryptoKey stored in
// IndexedDB (never localStorage, never serialized as JWK). It can be used to
// sign but its raw bytes can never be read by JS — so an XSS payload cannot
// exfiltrate it. Only the public key is exported (to the backend).

const DB_NAME = "dapledger";
const STORE = "keys";

function bufToB64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function keyName(email) {
  return (email || "").toLowerCase();
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, "readwrite").objectStore(STORE).put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// Generate a fresh non-extractable keypair, persist the private CryptoKey in
// IndexedDB, and return the public key as base64 DER (SPKI) for the backend.
export async function registerBiometricKey(email) {
  const pair = await window.crypto.subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    false, // private key is NON-EXTRACTABLE
    ["sign", "verify"]
  );
  await idbSet(keyName(email), pair.privateKey);
  const spki = await window.crypto.subtle.exportKey("spki", pair.publicKey);
  return bufToB64(spki);
}

export async function hasLocalKey(email) {
  try {
    return !!(await idbGet(keyName(email)));
  } catch (e) {
    console.error("hasLocalKey check failed:", e);
    return false;
  }
}

// Attempt a real device biometric gesture (fingerprint / face) via WebAuthn.
// Returns true if performed, false if unavailable — never throws.
export async function requestBiometricGesture(email) {
  try {
    if (!window.PublicKeyCredential) return false;
    const available =
      await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    if (!available) return false;
    const challenge = window.crypto.getRandomValues(new Uint8Array(32));
    await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: { name: "Dap Ledger", id: window.location.hostname },
        user: {
          id: new TextEncoder().encode(email),
          name: email,
          displayName: email,
        },
        // Offer both ES256 and RS256 — some Android authenticators reject ES256-only.
        pubKeyCredParams: [
          { type: "public-key", alg: -7 },
          { type: "public-key", alg: -257 },
        ],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "required",
        },
        timeout: 30000,
      },
    });
    return true;
  } catch (e) {
    console.warn("Biometric gesture unavailable, falling back to device key:", e?.name || e);
    return false;
  }
}

// Sign the canonical dap message with the non-extractable device key.
export async function signMessage(email, message) {
  const priv = await idbGet(keyName(email));
  if (!priv) throw new Error("No biometric key on this device");
  const sig = await window.crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-256" },
    priv,
    new TextEncoder().encode(message)
  );
  return bufToB64(sig); // base64 raw r||s (64 bytes)
}

export function canonicalMessage(dap) {
  const ref = dap.counterparty_ref || dap.counterparty_email;
  return `${dap.dap_id}|${dap.title}|${dap.terms}|${dap.creator_email}|${ref}`;
}
